import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaBars, FaTimes, FaHeart, FaBell, FaCreditCard, FaHome, FaCalendarAlt, FaClipboardList, FaChartBar, FaUsers, FaTasks, FaRobot, FaTrash, FaHeadset, FaChevronDown, FaGlobe } from 'react-icons/fa';
import { getApiUrl } from '../config/api';
import { getProfileImageUrl } from '../utils/imageHelper';
import { loadWishlistFavorites } from '../utils/wishlistStorage';
import { setGoogleTranslateLocale } from '../utils/languagePreference';
import { clearCompanyStaffSession, mergeUserWithStaffSession } from '../utils/companyStaffSession';
import { unsubscribePushNotifications } from '../services/pushNotificationService';
import { MOBILE_MEDIA_QUERY } from '../utils/breakpoints';
import AccountSubscriptionPage from '../pages/AccountSubscriptionPage';
import DeleteAccountModal from './DeleteAccountModal';
import './Navbar.css';

const HOST_DASHBOARD_ROLES = new Set(['host', 'company_admin', 'company_manager', 'company_staff']);
const COMPANY_WORKSPACE_ROLES = new Set(['company_admin', 'company_manager', 'company_staff']);
const COMPANY_DISPLAY_NAMES = {
  'abc-ltd': 'ABC Ltd',
  'pqr-ltd': 'PQR Ltd',
  'xyz-ltd': 'XYZ Ltd',
};
const COMPANY_EMAIL_HINTS = [
  { needle: '.abc', name: 'ABC Ltd' },
  { needle: '.pqr', name: 'PQR Ltd' },
  { needle: '.xyz', name: 'XYZ Ltd' },
];
const NOTIFICATION_FETCH_TIMEOUT_MS = 7000;

const MEETING_NAV_ITEMS = [
  {
    to: '/meetings/workspace',
    label: 'Home',
    icon: FaHome,
    match: (pathname) => pathname === '/meetings/workspace',
  },
  {
    to: '/meetings/book',
    label: 'Book Room',
    icon: FaCalendarAlt,
    match: (pathname) => pathname === '/meetings/book',
  },
  {
    to: '/meetings/my-bookings',
    label: 'My Bookings',
    icon: FaClipboardList,
    match: (pathname) => pathname === '/meetings/my-bookings',
  },
  {
    to: '/meeting-book',
    label: 'Schedule',
    icon: FaChartBar,
    match: (pathname) => pathname === '/meeting-book' || pathname === '/meetings/schedule',
  },
  {
    to: '/meetings/create-team',
    label: 'Create Your Team',
    icon: FaUsers,
    match: (pathname) => pathname === '/meetings/create-team',
  },
  {
    to: '/meetings/status',
    label: 'Status',
    icon: FaTasks,
    match: (pathname) => pathname === '/meetings/status',
  },
];

const Navbar = ({ isScrolled, onSignInClick, showLoginModal = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [meetingMenuOpen, setMeetingMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isMobileNav, setIsMobileNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MEDIA_QUERY).matches
  );
  const notificationFetchErrorLoggedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Languages for Google Translate (must match index.html includedLanguages)
  const LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'hi', label: 'हिन्दी' },
    { code: 'kn', label: 'ಕನ್ನಡ' },
    { code: 'ta', label: 'தமிழ்' },
    { code: 'te', label: 'తెలుగు' },
    { code: 'bn', label: 'বাংলা' },
    { code: 'mr', label: 'मराठी' },
    { code: 'ml', label: 'മലയാളം' },
    { code: 'gu', label: 'ગુજરાતી' },
    { code: 'pa', label: 'ਪੰਜਾਬੀ' },
    { code: 'ur', label: 'اردو' },
    { code: 'or', label: 'ଓଡ଼ିଆ' },
    { code: 'sa', label: 'संस्कृतम्' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
    { code: 'de', label: 'Deutsch' },
    { code: 'ar', label: 'العربية' }
  ];

  const getCurrentLangCode = () => {
    try {
      const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]*)/);
      if (match?.[1]) {
        const parts = decodeURIComponent(match[1]).split('/').filter(Boolean);
        if (parts.length >= 2) return parts[parts.length - 1].toUpperCase();
      }
    } catch (e) {}
    return (document.documentElement.lang || 'en').toUpperCase();
  };

  const currentLangCode = getCurrentLangCode();

  const handleLanguageSelect = (code) => {
    setGoogleTranslateLocale(code === 'en' ? 'en' : code);
    document.documentElement.lang = code || 'en';
    setLangMenuOpen(false);
    window.location.reload();
  };

  const openAccountModal = () => {
    setAccountModalOpen(true);
  };

  // Check if current route is a meeting page
  const isMeetingPage = location.pathname.startsWith('/meeting');

  useEffect(() => {
    const loadUser = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          const parsedUser = mergeUserWithStaffSession(JSON.parse(storedUser));
          setCurrentUser(parsedUser);

          // ✅ SECURITY: Try to derive wishlist count from user-specific localStorage favorites
          try {
            // Get user-specific favorites key
            const getFavoritesKey = () => {
              try {
                return `favorites_${parsedUser._id || parsedUser.id}`;
              } catch (error) {
                return 'favorites';
              }
            };

            const favoritesKey = getFavoritesKey();
            const favorites = loadWishlistFavorites(favoritesKey);
            setWishlistCount(favorites.length);
          } catch (error) {
            setWishlistCount(0);
          }

          // Migrate Google profile picture to local (avoids 429 from lh3.googleusercontent.com)
          const pic = parsedUser?.profilePicture;
          if (pic && typeof pic === 'string' && pic.includes('googleusercontent.com')) {
            const uid = parsedUser._id || parsedUser.id;
            const key = `migrateAttempted_${uid}`;
            if (!sessionStorage.getItem(key)) {
              sessionStorage.setItem(key, '1');
              fetch(getApiUrl('/api/auth/migrate-google-profile-picture'), {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${storedToken}` }
              })
                .then((r) => r.ok && r.json())
                .then((data) => {
                  if (data?.success && data?.user?.profilePicture) {
                    const updated = { ...parsedUser, profilePicture: data.user.profilePicture };
                    localStorage.setItem('user', JSON.stringify(updated));
                    setCurrentUser(updated);
                    window.dispatchEvent(new CustomEvent('userProfileUpdated'));
                  }
                })
                .catch(() => {});
            }
          }

          // Load notification count from API (guest only – host notifications stay on host dashboard)
          fetchNotificationCount(parsedUser, storedToken);
        } else {
          setCurrentUser(null);
          setWishlistCount(0);
          setNotificationCount(0);
        }
      } catch {
        setCurrentUser(null);
        setWishlistCount(0);
        setNotificationCount(0);
      }
    };

    loadUser();

    // Poll for notification updates every 10 seconds
    const notificationInterval = setInterval(() => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      if (storedUser && storedToken) {
        const parsedUser = JSON.parse(storedUser);
        fetchNotificationCount(parsedUser, storedToken);
      }
    }, 20000);

    const handleStorage = (event) => {
      if (event.key === 'user' || event.key === 'token') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('storage', loadUser);
    
    // Listen for notification badge update (when user marks as read on NotificationsPage)
    const handleBadgeUpdate = (e) => {
      if (e.detail?.unreadCount !== undefined) {
        setNotificationCount(e.detail.unreadCount);
      } else {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        if (storedUser && storedToken) {
          fetchNotificationCount(JSON.parse(storedUser), storedToken);
        }
      }
    };
    window.addEventListener('notificationBadgeUpdate', handleBadgeUpdate);

    // Listen for profile updates (when user saves changes on Account page)
    const handleProfileUpdate = () => loadUser();
    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    
    return () => {
      clearInterval(notificationInterval);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('storage', loadUser);
      window.removeEventListener('notificationBadgeUpdate', handleBadgeUpdate);
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  useEffect(() => {
    setMeetingMenuOpen(false);
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const syncMobile = () => setIsMobileNav(mq.matches);
    syncMobile();
    mq.addEventListener('change', syncMobile);
    return () => mq.removeEventListener('change', syncMobile);
  }, []);

  useEffect(() => {
    if (!isUserMenuOpen || !isMobileNav) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsUserMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isUserMenuOpen, isMobileNav]);

  useEffect(() => {
    if (!isMenuOpen || !isMobileNav) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMenuOpen, isMobileNav]);

  // Fetch notification count from API – always guest context so guest pages never show host notifications
  const fetchNotificationCount = async (user, token) => {
    if (!user || !token) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), NOTIFICATION_FETCH_TIMEOUT_MS);
    try {
      const url = `${getApiUrl('/api/notifications/my-notifications')}?userType=guest&unreadOnly=true`;
      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: controller.signal
        }
      );

      if (!response.ok) {
        throw new Error(`notification_count_http_${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        const count = result.unreadCount || 0;
        setNotificationCount(count);
        notificationFetchErrorLoggedRef.current = false;
        
        // Store in localStorage (bell links to guest notifications page)
        const userId = user._id || user.id;
        const notificationCountKey = `notificationCount_guest_${userId}`;
        localStorage.setItem(notificationCountKey, count.toString());
      }
    } catch (error) {
      if (!notificationFetchErrorLoggedRef.current && process.env.NODE_ENV === 'development') {
        console.warn('Notification count fetch skipped:', error?.message || error);
        notificationFetchErrorLoggedRef.current = true;
      }
      // Fallback to localStorage (bell shows guest notification count)
      try {
        const userId = user._id || user.id;
        const notificationCountKey = `notificationCount_guest_${userId}`;
        const storedCount = localStorage.getItem(notificationCountKey);
        if (storedCount) {
          setNotificationCount(parseInt(storedCount) || 0);
        }
      } catch (err) {
        setNotificationCount(0);
      }
    } finally {
      clearTimeout(timeoutId);
    }
  };

  const isAuthenticated = !!currentUser;
  const isHost = HOST_DASHBOARD_ROLES.has(String(currentUser?.role || '')) || Boolean(currentUser?.company?._id || currentUser?.company);
  const isCompanyWorkspaceUser = COMPANY_WORKSPACE_ROLES.has(String(currentUser?.role || ''));
  const activeMeetingNavItem = MEETING_NAV_ITEMS.find((item) => item.match(location.pathname));
  const activeMeetingMenuLabel = activeMeetingNavItem?.label || 'Home';
  const getCompanyDisplayName = (user) => {
    if (!user) return '';
    const company = user.company;
    if (company && typeof company === 'object') {
      const companyName = company.profile?.displayName || company.name || COMPANY_DISPLAY_NAMES[company.slug];
      if (companyName) return companyName;
    }

    const companySlug = typeof company === 'string' ? company : user.companySlug;
    if (companySlug && COMPANY_DISPLAY_NAMES[String(companySlug).toLowerCase()]) {
      return COMPANY_DISPLAY_NAMES[String(companySlug).toLowerCase()];
    }

    const emailLocalPart = String(user.email || '').toLowerCase().split('@')[0] || '';
    const matchedHint = COMPANY_EMAIL_HINTS.find((hint) => emailLocalPart.includes(hint.needle));
    return matchedHint?.name || '';
  };

  const shortenCompanyName = (name) => {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';

    const withoutSuffix = trimmed
      .replace(/\s*,?\s*\b(pvt\.?\s*ltd\.?|private\s+limited|inc\.?|corp\.?|llc)\s*\.?\s*$/i, '')
      .trim();
    const words = withoutSuffix.split(/\s+/).filter(Boolean);
    if (words.length <= 2) return withoutSuffix;

    const shortSecond = words[1].length > 4 ? words[1].slice(0, 4) : words[1];
    return `${words[0]} ${shortSecond}`;
  };

  const getCompanyShortDisplayName = (user) => {
    const fullName = getCompanyDisplayName(user);
    return fullName ? shortenCompanyName(fullName) : '';
  };

  const getStaffDisplayName = (user) => {
    const name = String(user?.staffProfile?.name || '').trim();
    return name || '';
  };

  const getAccountDisplayName = () => {
    if (!currentUser) return 'Profile';
    const staffName = getStaffDisplayName(currentUser);
    const companyName = getCompanyDisplayName(currentUser);
    if (staffName && companyName) return `${staffName} • ${companyName}`;
    if (staffName) return staffName;
    if (companyName) return companyName;
    return String(currentUser.name || currentUser.email || 'Profile').trim();
  };

  const getUserAvatarLabel = () => {
    if (!currentUser) return 'Profile';
    const companyName = getCompanyShortDisplayName(currentUser);
    return companyName || 'Profile';
  };

  const getUserInitials = () => {
    if (!currentUser) return 'U';
    const base =
      getStaffDisplayName(currentUser) ||
      getCompanyDisplayName(currentUser) ||
      currentUser.name ||
      currentUser.email ||
      '';
    const name = String(base).trim();
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!currentUser) return 'Profile';
    const staffName = getStaffDisplayName(currentUser);
    const base = String(
      staffName ||
      getCompanyShortDisplayName(currentUser) ||
      currentUser.name ||
      currentUser.email ||
      ''
    ).trim();
    if (!base) return 'Profile';
    return base;
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
    setIsMenuOpen(false);
  };

  const confirmLogout = async () => {
    await unsubscribePushNotifications();
    // ✅ SECURITY: Clear all user-related data on logout
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        const userId = userObj._id || userObj.id;
        localStorage.removeItem(`officeSpaceAnswered_${userId}`);
        localStorage.removeItem(`notificationCount_guest_${userId}`);
        localStorage.removeItem(`notificationCount_host_${userId}`);
        // Clear user-specific popup flag
        localStorage.removeItem(`meetingHomePopupShown_${userId}`);
      }
    } catch (error) {
      console.error('Error clearing user-specific data:', error);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearCompanyStaffSession();
    // Clear old non-user-specific popup flag (for backward compatibility)
    localStorage.removeItem('meetingHomePopupShown');
    
    // Clear all localStorage keys that might contain user data
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('officeSpaceAnswered_') || key.startsWith('user_') || key.startsWith('booking_') || key.startsWith('notificationCount_') || key.startsWith('lastSearch_') || key.startsWith('recentSearches_') || key.startsWith('meetingHomePopupShown_')) {
        localStorage.removeItem(key);
      }
    });
    setCurrentUser(null);
    setShowLogoutModal(false);
    navigate('/');
    window.location.reload();
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const closeUserMenu = () => setIsUserMenuOpen(false);

  const toggleUserMenu = () => {
    setLangMenuOpen(false);
    setIsUserMenuOpen((open) => !open);
  };

  const renderUserMenuHeader = () => {
    if (!currentUser) {
      return <span className="user-menu-name">Profile</span>;
    }
    const staffName = getStaffDisplayName(currentUser);
    const companyName = getCompanyDisplayName(currentUser);
    if (staffName && companyName) {
      return (
        <>
          <span className="user-menu-staff">{staffName}</span>
          <span className="user-menu-company">{companyName}</span>
        </>
      );
    }
    return <span className="user-menu-name">{getAccountDisplayName()}</span>;
  };

  const renderUserMenuDropdown = () => (
    <div className="user-menu-dropdown" role="menu">
      <div className="user-menu-header">{renderUserMenuHeader()}</div>
      <div className="user-menu-ai-wrap">
        <Link
          to="/ai-assistant"
          className="user-menu-item user-menu-item-ai"
          role="menuitem"
          onClick={closeUserMenu}
        >
          <FaRobot className="user-menu-item-icon" aria-hidden="true" />
          AI Stay Finder
        </Link>
      </div>
      <button
        type="button"
        className="user-menu-item user-menu-item-mobile-only"
        onClick={() => {
          closeUserMenu();
          openAccountModal();
        }}
      >
        <FaCreditCard className="user-menu-item-icon" />
        Subscription
      </button>
      <div className="user-menu-links">
      <Link to="/trips" className="user-menu-item" onClick={closeUserMenu}>
        My Bookings
      </Link>
      {isMeetingPage ? (
        <Link to="/" className="user-menu-item" onClick={closeUserMenu}>
          Book Your Stay
        </Link>
      ) : (
        <Link to="/meeting-home" className="user-menu-item" onClick={closeUserMenu}>
          Book Your Meeting
        </Link>
      )}
      <Link to="/wishlist" className="user-menu-item" onClick={closeUserMenu}>
        Saved Stays
      </Link>
      {isCompanyWorkspaceUser && (
        <Link to="/workspace" className="user-menu-item" onClick={closeUserMenu}>
          Workspace
        </Link>
      )}
      <Link to="/host" className="user-menu-item" onClick={closeUserMenu}>
        List Property
      </Link>
      {isMeetingPage && (
        <Link to="/meetings/add-your-space" className="user-menu-item" onClick={closeUserMenu}>
          Add Your Space
        </Link>
      )}
      <Link to="/account" className="user-menu-item" onClick={closeUserMenu}>
        Profile Settings
      </Link>
      <Link to="/help" className="user-menu-item" onClick={closeUserMenu}>
        Help &amp; Support
      </Link>
      <Link to="/feedback" className="user-menu-item" onClick={closeUserMenu}>
        Feedback
      </Link>
      {!isHost && (
        <Link to="/host" className="user-menu-item" onClick={closeUserMenu}>
          Switch to Host View
        </Link>
      )}
      <button
        type="button"
        className="user-menu-item user-menu-item-delete"
        onClick={() => {
          closeUserMenu();
          setShowDeleteAccountModal(true);
        }}
      >
        <FaTrash className="user-menu-item-icon" aria-hidden="true" />
        Delete my account
      </button>
      <button type="button" className="user-menu-item logout" onClick={handleLogout}>
        Log out
      </button>
      </div>
    </div>
  );

  return (
    <motion.nav
      className={`navbar ${isScrolled ? 'scrolled' : ''}${isMeetingPage ? ' navbar--meeting-route' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {isMenuOpen && (
        <button
          type="button"
          className="navbar-menu-backdrop"
          aria-label="Close menu"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
      <div className="navbar-container">

      <Link to="/" className="navbar-logo" aria-label="UBSAAPP home">
        <img
          src="/ubsaapp-topnav-logo.png"
          alt="UBSAAPP icon"
          className="navbar-logo-icon"
        />
      </Link>

        {/* Center - Meeting room links (middle of navbar) */}
        <div className={`navbar-menu navbar-menu-center ${isMenuOpen ? 'active' : ''}`}>
          {!isMeetingPage && (
            <>
              <NavLink
                to="/"
                className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaHome className="user-menu-item-icon" aria-hidden="true" />
                Home
              </NavLink>
              <NavLink
                to="/meeting-home"
                className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaCalendarAlt className="user-menu-item-icon" aria-hidden="true" />
                Book Your Meeting
              </NavLink>
              <NavLink
                to="/ai-assistant"
                className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaRobot className="user-menu-item-icon" aria-hidden="true" />
                AI Stay Finder
              </NavLink>
              {isAuthenticated && (
                <>
                  <span className="navbar-mobile-section-title">My account</span>
                  <NavLink
                    to="/trips"
                    className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaClipboardList className="user-menu-item-icon" aria-hidden="true" />
                    My Bookings
                  </NavLink>
                  <NavLink
                    to="/wishlist"
                    className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaHeart className="user-menu-item-icon" aria-hidden="true" />
                    Saved Stays
                  </NavLink>
                  <NavLink
                    to="/account"
                    className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <FaUser className="user-menu-item-icon" aria-hidden="true" />
                    Profile Settings
                  </NavLink>
                </>
              )}
              <span className="navbar-mobile-section-title">Support</span>
              <NavLink
                to="/help"
                className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                <FaHeadset className="user-menu-item-icon" aria-hidden="true" />
                Help &amp; Support
              </NavLink>
              {!isAuthenticated && (
                <NavLink
                  to="/login"
                  className={({ isActive }) => `navbar-link navbar-mobile-link${isActive ? ' active' : ''}`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  <FaUser className="user-menu-item-icon" aria-hidden="true" />
                  Sign in
                </NavLink>
              )}
            </>
          )}
          {isAuthenticated && isMeetingPage && (
            <div className="meeting-dropdown-menu">
              {meetingMenuOpen && (
                <button
                  type="button"
                  className="meeting-dropdown-overlay"
                  aria-label="Close meeting menu"
                  onClick={() => setMeetingMenuOpen(false)}
                />
              )}
              <div
                className={`meeting-dropdown-trigger${meetingMenuOpen ? ' open' : ''}`}
              >
                <button
                  type="button"
                  className="meeting-dropdown-trigger-main"
                  onClick={() => {
                    navigate('/meetings/workspace');
                    setMeetingMenuOpen(false);
                  }}
                >
                  <span className="meeting-dropdown-trigger-copy">
                    <span className="meeting-dropdown-trigger-kicker">Private Workspace Menu</span>
                    <span className="meeting-dropdown-trigger-label">{activeMeetingMenuLabel}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="meeting-dropdown-trigger-caret-btn"
                  aria-label="Open workspace menu"
                  aria-haspopup="true"
                  aria-expanded={meetingMenuOpen}
                  onClick={() => setMeetingMenuOpen((open) => !open)}
                >
                  <FaChevronDown className="meeting-dropdown-trigger-caret" aria-hidden="true" />
                </button>
              </div>

              {meetingMenuOpen && (
                <div className="meeting-dropdown-panel" role="menu" aria-label="Meeting navigation">
                  {MEETING_NAV_ITEMS.map(({ to, label, icon: Icon, match }) => (
                    <NavLink
                      key={to}
                      to={to}
                      role="menuitem"
                      onClick={() => setMeetingMenuOpen(false)}
                      className={({ isActive }) =>
                        `meeting-dropdown-link${isActive || match(location.pathname) ? ' active' : ''}`
                      }
                    >
                      <span className="meeting-dropdown-link-icon-wrap">
                        <Icon className="meeting-dropdown-link-icon" aria-hidden />
                      </span>
                      <span className="meeting-dropdown-link-copy">
                        <span className="meeting-dropdown-link-label">{label}</span>
                        <span className="meeting-dropdown-link-meta">Open section</span>
                      </span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="navbar-actions">
          {/* Language / Translate - globe + code + chevron */}
          <div className="navbar-lang-wrap">
            <button
              type="button"
              className="navbar-lang-btn"
              aria-label="Choose language"
              aria-haspopup="true"
              aria-expanded={langMenuOpen}
              onClick={() => setLangMenuOpen((o) => !o)}
              title="Language"
            >
              <FaGlobe className="navbar-lang-globe" aria-hidden="true" />
              <span className="navbar-lang-code">{currentLangCode}</span>
              <FaChevronDown className="navbar-lang-chevron" aria-hidden="true" />
            </button>
            {langMenuOpen && (
              <>
                <div className="navbar-lang-backdrop" onClick={() => setLangMenuOpen(false)} aria-hidden="true" />
                <div className="navbar-lang-dropdown" role="menu">
                  <div className="navbar-lang-dropdown-title">Language / ಭಾಷೆ</div>
                  {LANGUAGES.map(({ code, label }) => (
                    <button
                      key={code}
                      type="button"
                      className="navbar-lang-option"
                      onClick={() => handleLanguageSelect(code)}
                      role="menuitem"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Subscription button - next to language button */}
          <button
            type="button"
            className="navbar-subscription-btn desktop-only"
            onClick={openAccountModal}
            aria-label="Subscription"
          >
            <FaCreditCard className="subscription-icon" />
            <span className="subscription-text">Subscription</span>
          </button>

          {!isAuthenticated ? (
            <>
              {showLoginModal && onSignInClick ? (
                <button
                  onClick={onSignInClick}
                  className="navbar-link sign-in-btn"
                  style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  <FaUser className="user-icon" />
                  <span>Sign in</span>
                </button>
              ) : (
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `navbar-link ${isActive ? 'active' : ''}`
                  }
                >
                  <FaUser className="user-icon" />
                  <span>Sign in</span>
                </NavLink>
              )}

              {/* Become a Host CTA for guests (not logged-in) */}
              <button
                className="btn-host"
                onClick={(e) => {
                  e.preventDefault();
                  // Store redirect path to /host
                  localStorage.setItem('authRedirectPath', '/host');
                  // If onSignInClick is provided (from MeetingHome), use it
                  if (onSignInClick) {
                    onSignInClick();
                  } else {
                    // Otherwise, navigate to /host page (which will show login prompt)
                    navigate('/host');
                  }
                }}
              >
                Become a Host
              </button>
            </>
          ) : (
            <>
              {/* Wishlist icon (Saved stays) */}
              <Link to="/wishlist" className="icon-button" aria-label="Saved stays">
                <FaHeart />
                {wishlistCount > 0 && (
                  <span className="icon-badge">
                    {wishlistCount > 9 ? '9+' : wishlistCount}
                  </span>
                )}
              </Link>

              {/* Notifications icon */}
              <button
                type="button"
                className="icon-button"
                aria-label="Notifications"
                onClick={() => navigate('/notifications')}
              >
                <FaBell />
                {notificationCount > 0 && (
                  <span className="icon-badge">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>

              {/* Switch to Host View - show for all logged-in users */}
              {isAuthenticated && (
                <button 
                  className="btn-host desktop-only"
                  onClick={() => {
                    // Navigate to host page - user is already logged in
                    navigate('/host');
                  }}
                >
                  {isHost ? 'list properties' : 'Become a Host'}
                </button>
              )}

              {/* User avatar & dropdown */}
              <div className="navbar-user">
                <button
                  type="button"
                  className={`user-avatar-button ${isUserMenuOpen ? 'open' : ''}`}
                  onClick={toggleUserMenu}
                  aria-haspopup="true"
                  aria-expanded={isUserMenuOpen}
                  aria-label="Open profile menu"
                >
                  <div className="user-avatar-circle">
                    {getProfileImageUrl(currentUser?.profilePicture) ? (
                      <img 
                        src={getProfileImageUrl(currentUser.profilePicture)} 
                        alt={getUserDisplayName()} 
                        className="user-avatar-image"
                      />
                    ) : (
                      <span>{getUserInitials()}</span>
                    )}
                  </div>
                  <div className="user-avatar-meta desktop-only">
                    <span className="user-avatar-label">{getUserAvatarLabel()}</span>
                    <span className="user-avatar-name">{getUserDisplayName()}</span>
                  </div>
                  <FaChevronDown className="user-avatar-caret desktop-only" aria-hidden="true" />
                </button>
                {isUserMenuOpen && !isMobileNav && renderUserMenuDropdown()}
              </div>
              {isUserMenuOpen && isMobileNav && typeof document !== 'undefined' && ReactDOM.createPortal(
                <>
                  <button
                    type="button"
                    className="user-menu-backdrop"
                    aria-label="Close profile menu"
                    onClick={closeUserMenu}
                  />
                  <div className="user-menu-portal">{renderUserMenuDropdown()}</div>
                </>,
                document.body
              )}
            </>
          )}

          {!isMeetingPage && !isMobileNav && (
          <button 
            className="menu-toggle"
            type="button"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          )}
        </div>

      </div>

      {accountModalOpen && (
        <AccountSubscriptionPage onClose={() => setAccountModalOpen(false)} />
      )}

      {showDeleteAccountModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteAccountModal(false)}
          onDeleted={() => {
            setShowDeleteAccountModal(false);
            setCurrentUser(null);
            navigate('/');
            window.location.reload();
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && ReactDOM.createPortal(
        <div className="logout-modal-overlay" onClick={cancelLogout}>
          <div className="logout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="logout-modal-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </div>
            <h3>Confirm Logout</h3>
            <p>Are you sure you want to logout?</p>
            <div className="logout-modal-actions">
              <button type="button" className="logout-btn-no" onClick={cancelLogout}>
                No
              </button>
              <button type="button" className="logout-btn-yes" onClick={confirmLogout}>
                Yes
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.nav>
  );
};

export default Navbar;
